import { ThreadProjectActions } from "./ThreadProjectActions";
import { type ScopedThreadRef, type ThreadId } from "@cmmd-forge/contracts";
import { type ComponentProps, type ReactElement } from "react";
import { type DraftId } from "../../draftId";
import { type Project, type ProjectScript, type Thread } from "../../types";
import { usePublishBreadcrumbLabel } from "~/components/breadcrumb/globalBreadcrumbStore";
import { AppTopBarActions } from "../appTopBarActionSlot";
import GitActionsControl from "../GitActionsControl";
import { ChatHeader } from "./ChatHeader";

export function ChatActiveThreadHeader(props: {
  readonly activeProject?: Project | null | undefined;
  readonly onRunScript?: ((script: ProjectScript) => Promise<void>) | undefined;
  readonly activeThreadTitle: string;
  readonly activeThreadKind: Thread["kind"];
  readonly activeThreadWorkerRole: Thread["workerRole"];
  readonly activeThreadParentId: ThreadId | null;
  readonly activeThreadParentTitle: string | null;
  readonly activeThreadWorkerCount: number;
  readonly jumpToParentShortcutLabel: string | null;
  readonly onJumpToParent: () => void;
  readonly isServerThread: boolean;
  readonly canOpenRightSidebar: boolean;
  readonly previewOpen: boolean;
  readonly canvasOpen: boolean;
  readonly diffOpen: boolean;
  readonly planOpen: boolean;
  readonly terminalOpen: boolean;
  readonly terminalRunningCount: number;
  readonly activeAgentCount: number;
  readonly onTogglePreview: () => void;
  readonly onToggleCanvas: () => void;
  readonly onToggleDiff: () => void;
  readonly onTogglePlan: () => void;
  readonly onToggleTerminal: () => void;
  readonly previewShortcutLabel: string | null;
  readonly canvasShortcutLabel: string | null;
  readonly diffShortcutLabel: string | null;
  readonly planShortcutLabel: string | null;
  readonly terminalShortcutLabel: string | null;
  readonly showPanelLauncher: boolean;
  readonly gitCwd: string | null;
  readonly activeThreadRef: ScopedThreadRef | null;
  readonly routeKind: "server" | "draft";
  readonly draftId: DraftId | undefined;
}): ReactElement {
  // The bar can derive a saved thread's title from the store, but a draft's
  // lives only in the composer session — and the draft crumb would otherwise
  // read "New thread" forever while this surface displays the real name.
  // Publishing from here covers both route kinds with one path.
  usePublishBreadcrumbLabel("thread", props.activeThreadTitle);
  return (
    <AppTopBarActions>
      <ChatHeader
        identity={{
          kind: props.activeThreadKind,
          workerRole: props.activeThreadWorkerRole,
          parentId: props.activeThreadParentId,
          parentTitle: props.activeThreadParentTitle,
          workerCount: props.activeThreadWorkerCount,
        }}
        jumpToParentShortcutLabel={props.jumpToParentShortcutLabel}
        onJumpToParent={props.onJumpToParent}
        panels={{
          isServerThread: props.isServerThread,
          canOpenRightSidebar: props.canOpenRightSidebar,
          previewOpen: props.previewOpen,
          canvasOpen: props.canvasOpen,
          diffOpen: props.diffOpen,
          planOpen: props.planOpen,
          terminalOpen: props.terminalOpen,
          terminalRunningCount: props.terminalRunningCount,
          activeAgentCount: props.activeAgentCount,
          showPanelLauncher: props.showPanelLauncher,
        }}
        onTogglePreview={props.onTogglePreview}
        onToggleTerminal={props.onToggleTerminal}
        terminalShortcutLabel={props.terminalShortcutLabel}
        /* `compact={false}` on purpose: `compact` hard-disables the responsive
           label rules, which is why these rendered as bare icons at every
           width. The labels are container-query gated on `header-actions`
           (opened by the bar root), so they appear only when the bar is
           actually wide enough — the behaviour `compact` was standing in for. */
        projectActions={renderProjectActions(props)}
      />
    </AppTopBarActions>
  );
}

function renderProjectActions(props: ComponentProps<typeof ChatActiveThreadHeader>) {
  const scripts =
    props.activeProject && props.onRunScript ? (
      <ThreadProjectActions
        project={props.activeProject}
        cwd={props.gitCwd}
        onRunScript={props.onRunScript}
      />
    ) : null;
  const git =
    props.gitCwd && props.activeThreadRef ? (
      <GitActionsControl
        gitCwd={props.gitCwd}
        activeThreadRef={props.activeThreadRef}
        compact={false}
        {...(props.routeKind === "draft" && props.draftId ? { draftId: props.draftId } : {})}
      />
    ) : null;
  return scripts || git ? (
    <>
      {scripts}
      {git}
    </>
  ) : null;
}
